import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { ChoiceGroup } from "../../../primitives/inputs/ChoiceGroup";
import { Note } from "../../../primitives/data-display/Note";
import { gaps } from "../../../primitives/spacing";
import { PanelContainedButton } from "./panel_shared";

interface Props {
  labels: string[];
  selected: string[];
  /** Several labels may be selected (checkboxes); otherwise exactly one (radio buttons). */
  multiple: boolean;
  focusIndex: number;
  /** Per-label rubric text, keyed by label; shown as a hover tooltip on each option. */
  labelDescriptions?: Record<string, string>;
  onFocusChange: (i: number) => void;
  onChange: (selection: string[]) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Label selector for override mode. ↑/↓ and Space are handled by the choice group; Enter and Esc
 * by the parent's keydown listener. The rows take the clicks, so the inputs never take focus away
 * from that listener.
 */
export default function OverrideMode({
  labels,
  selected,
  multiple,
  focusIndex,
  labelDescriptions,
  onFocusChange,
  onChange,
  onConfirm,
  onCancel,
}: Props) {
  return (
    <Stack gap={gaps.snug}>
      <Note>
        {multiple
          ? "Select labels — Space to toggle · Enter to confirm · Esc to cancel"
          : "Select one label — Space to select · Enter to confirm · Esc to cancel"}
      </Note>
      <ChoiceGroup
        options={labels}
        value={selected}
        multiple={multiple}
        onChange={onChange}
        renderTooltip={(label) => labelDescriptions?.[label]}
        disablePointerEvents
        focusedIndex={focusIndex}
        onFocusChange={onFocusChange}
        keyboardNavigation
      />
      <Stack direction="row" gap={gaps.snug}>
        <PanelContainedButton variant="contained" size="small" onClick={onConfirm}>
          Confirm (↵)
        </PanelContainedButton>
        <Button variant="outlined" color="inherit" size="small" onClick={onCancel}>
          Cancel (Esc)
        </Button>
      </Stack>
    </Stack>
  );
}
